import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

const ROWS = 6, COLS = 7;
const empty = () => Array.from({ length: ROWS }, () => Array(COLS).fill(null));

function checkWin(b, r, c, p) {
  const dirs = [[0, 1], [1, 0], [1, 1], [1, -1]];
  for (const [dr, dc] of dirs) {
    let count = 1;
    const cells = [[r, c]];
    for (const sign of [1, -1]) {
      let rr = r + dr * sign, cc = c + dc * sign;
      while (rr >= 0 && rr < ROWS && cc >= 0 && cc < COLS && b[rr][cc] === p) {
        count++; cells.push([rr, cc]); rr += dr * sign; cc += dc * sign;
      }
    }
    if (count >= 4) return cells;
  }
  return null;
}

export function ConnectFour() {
  const [board, setBoard] = useState(empty);
  const [turn, setTurn] = useState("R");
  const [scores, setScores] = useState({ R: 0, Y: 0 });
  const [result, setResult] = useState(null); // {player, cells} | "draw"
  const [winCells, setWinCells] = useState([]);

  const drop = (col) => {
    if (result) return;
    let row = -1;
    for (let r = ROWS - 1; r >= 0; r--) { if (!board[r][col]) { row = r; break; } }
    if (row === -1) return;
    const nb = board.map((rw) => [...rw]);
    nb[row][col] = turn;
    setBoard(nb);
    const win = checkWin(nb, row, col, turn);
    if (win) {
      setResult({ player: turn }); setWinCells(win);
      setScores((s) => ({ ...s, [turn]: s[turn] + 1 }));
    } else if (nb.every((rw) => rw.every(Boolean))) {
      setResult("draw");
    } else {
      setTurn(turn === "R" ? "Y" : "R");
    }
  };

  const nextRound = () => { setBoard(empty()); setTurn("R"); setResult(null); setWinCells([]); };
  const resetAll = () => { nextRound(); setScores({ R: 0, Y: 0 }); };

  const isWin = (r, c) => winCells.some(([wr, wc]) => wr === r && wc === c);

  return (
    <div className="flex flex-col items-center" data-testid="connect-four">
      <div className="flex items-center gap-6 mb-4 font-mono">
        <span className="font-display font-bold" style={{ color: "#FF003C" }}>RED: {scores.R}</span>
        <span className="text-zinc-500">vs</span>
        <span className="font-display font-bold" style={{ color: "#EFFF04" }}>YELLOW: {scores.Y}</span>
      </div>

      <div className="bg-zinc-800 p-2 rounded-xl border-2 border-zinc-700" style={{ width: "min(94vw, 380px)" }}>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}>
          {Array.from({ length: COLS }).map((_, c) => (
            <button key={c} onClick={() => drop(c)} data-testid={`c4-col-${c}`} className="flex flex-col gap-1.5 group">
              {Array.from({ length: ROWS }).map((_, r) => {
                const v = board[r][c];
                return (
                  <div key={r} className="aspect-square rounded-full border-2 transition-all"
                    style={{
                      background: v === "R" ? "#FF003C" : v === "Y" ? "#EFFF04" : "#09090b",
                      borderColor: isWin(r, c) ? "#39FF14" : "#3f3f46",
                      boxShadow: isWin(r, c) ? "0 0 12px #39FF14" : v ? `0 0 8px ${v === "R" ? "#FF003C" : "#EFFF04"}` : "none",
                    }} />
                );
              })}
            </button>
          ))}
        </div>
      </div>

      <div className="h-16 flex flex-col items-center justify-center mt-4">
        {result ? (
          <>
            <p className="font-display font-black text-xl uppercase mb-2" data-testid="c4-result">
              {result === "draw" ? "It's a draw!" : (
                <span style={{ color: result.player === "R" ? "#FF003C" : "#EFFF04" }}>
                  {result.player === "R" ? "Red" : "Yellow"} connects 4! 🏆
                </span>
              )}
            </p>
            <div className="flex gap-3">
              <Button onClick={nextRound} data-testid="c4-next" className="bg-neon-cyan text-black rounded-none font-bold uppercase h-9 text-xs">Next Round</Button>
              <Button onClick={resetAll} variant="outline" className="border-zinc-700 text-white rounded-none font-bold uppercase h-9 text-xs">
                <RotateCcw className="w-3 h-3 mr-1" /> Reset
              </Button>
            </div>
          </>
        ) : (
          <p className="font-mono text-zinc-400">
            Turn: <span className="font-bold" style={{ color: turn === "R" ? "#FF003C" : "#EFFF04" }}>{turn === "R" ? "Red" : "Yellow"}</span>
          </p>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono">Two players. Tap a column to drop your disc. Connect 4 to win!</p>
    </div>
  );
}
